<template>
  <div class="container">
    <transition name="fade" >
      <div class="column-center" v-if="show">
        <div class="section">
          <div
            v-if="showUnpaidMessage"
            class="unpaid-message"
          >
            <span>
              There's an issue with your payment method. Please visit your billing portal below.
            </span>
            <span
              class="button dismiss"
              @click="hasUnpaidMessageBeenDismissed = true"
            >
              Dismiss
            </span>
          </div>
          <div class="section-title">
            Your Team
          </div>
          <div class="section-body">
            
            <div class="section-subtitle">
              Team Name
            </div>
            <div
              :class="[{ hidden: !isEditingTeamName }, 'char-limit-row']"
            >
              Max {{ this.teamNameCharLimit }} characters
            </div>
            <div class="section-row" v-show="!isEditingTeamName">
              <span class="name-item">
                {{ teamInfo.teamName }}
              </span>
              <span
                v-if="isTeamLeader"
                class="edit-icon-container"
              >
                <font-awesome-icon
                  :icon="['fas', 'edit']"
                  class="edit-icon"
                  @click="editTeamName()"
                />
              </span>
            </div>
            
            <div class="section-row" v-show="isEditingTeamName">
              <input
                ref="teamNameInput"
                class="team-name-input"
                v-model="newTeamName"
                type="text"
                @input="clipInputString"
                :maxlength="teamNameCharLimit"
                placeholder="Update your team name"
                autocomplete="off"
                autocorrect="off"
                autofocus
                spellcheck="false"
                @keydown.enter.prevent="saveTeamName"
              >
              <SpinnerButton
                class="button narrow"
                :isDisabled="!newTeamName"
                @click="saveTeamName"
              >
                Save
              </SpinnerButton>
              <SpinnerButton
                class="button danger narrow"
                :showSpinner="false"
                @click="cancelTeamName"
              >
                Cancel
              </SpinnerButton>
            </div>

            <div class="section-subtitle">
              Plan 
            </div>
            <div class="hidden char-limit-row">
              placeholder text
            </div>
            <div class="section-row">
              <span class="name-item">
                {{plan}}
              </span>
            </div>
            <br>

          </div>
          <div class="section-body">
            <!-- these are here to update the text which shows how many members are in a time - the first two cases are for enterprise plans
            and the last one is for pro plans -->
            <div v-if="totalNumberOfSeats==='unlimited' && teamInfo.members.length == 0" class="section-subtitle">
              Team Members ({{teamInfo.members.length + 1}} member)
            </div>
            <div v-else-if="totalNumberOfSeats==='unlimited'" class="section-subtitle">
              Team Members ({{teamInfo.members.length + 1}} members)
            </div>
            <div v-else class="section-subtitle">
              Team Members ({{teamInfo.members.length + 1}} / {{totalNumberOfSeats}} members)
            </div>
            <div class="section-row">
              <SpinnerButton
                v-if="isTeamLeader"
                class="button right"
                :isDisabled="!isTeamLeader"
                @click="showInviteLinkModal = true"
              >
                Invite New Members
              </SpinnerButton>
            </div>
            <div
              v-if="'leader' in teamInfo"
              class="member-entry"
            >
              <div class="member-item">
                <div class="profile-picture">
                  <img
                    class="lazyload"
                    :src="defaultProfilePicture"
                    :data-src="teamInfo.leader.profileImageUrl"
                  >
                </div>
                <div class="member-name">
                  {{ teamInfo.leader.name }}
                </div>
                <div class="pill">
                  Team Leader
                </div>
              </div>
            </div>
            <div
              class="member-entry"
              v-for="[index, member] in teamInfo.members.entries()"
              :key="index"
            >
              <div class="member-item">
                <div class="profile-picture">
                  <img
                    class="lazyload"
                    :src="defaultProfilePicture"
                    :data-src="member.profileImageUrl"
                  >
                </div>
                <div class="member-name">
                  {{ member.name }}
                </div>
                <!-- <span
                  v-if="isTeamLeader"
                  class="button leader right"
                  @click="transferLeadershipToMember(index)"
                >
                  Make Leader
                </span> -->
                <SpinnerButton
                  v-if="isTeamLeader"
                  class="button leader right"
                  @click="transferLeadershipToMember(index)"
                >
                  Make Leader
                </SpinnerButton>
                <!-- <span
                  v-if="isTeamLeader"
                  class="button narrow danger"
                  @click="removeMemberFromTeam(index)"
                >
                  Remove
                </span> -->
                <SpinnerButton
                  v-if="isTeamLeader"
                  class="button danger narrow"
                  @click="removeMemberFromTeam(index)"
                >
                  Remove
                </SpinnerButton>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="isTeamLeader"
          class="section"
        >
          <div class="section-title">
            Manage Your Subscription
          </div>
          <div class="section-body">
            <div class="section-subtitle">
              Billing
            </div>
            <div class="section-row">
              <span>
                View invoices and manage payment methods
                <span
                  v-if="isTeamLeader && isSubscriptionUnpaid"
                  class="pill danger"
                >
                  Payment Issue
                </span>
              </span>
              <SpinnerButton
                class="button"
                :isProcessing="isRedirectingToPortal"
                @click="redirectToPortal"
              >
                Visit Billing Portal
              </SpinnerButton>
            </div>
            <div
              v-if="isSubscriptionActive && !isSubscriptionEnding && !isEnterpriseSubscription"
              class="section-subtitle"
            >
              Add or Remove Seats
            </div>
            <div
              v-if="isSubscriptionActive && !isSubscriptionEnding && !isEnterpriseSubscription"
              class="section-row"
            >
              <span>
                Add or remove available seats on your team
              </span>
              <SpinnerButton
                class="button"
                @click="showChangeNumSeatsModal = true"
              >
                Add or Remove Seats
              </SpinnerButton>
            </div>
            <div class="section-subtitle">
              Change Your Plan
            </div>
            <div class="section-row">
              <span>
                Compare available plans and switch
              </span>
              <SpinnerButton
                class="button"
                :isProcessing="isRedirectingToPlans"
                @click="$router.push({ name: 'pricing' }); isRedirectingToPlans = true;"
              >
                View Plans
              </SpinnerButton>
            </div>
            <div
              v-if="isSubscriptionActive && !isSubscriptionEnding"
              class="section-subtitle danger"
            >
              Cancel Your Subscription
            </div>
            <div
              v-if="isSubscriptionActive && !isSubscriptionEnding"
              class="section-row"
            >
              <span>
                Cancel your subscription and disband your team
              </span>
              <span
                class="button danger"
                @click="showCancelSubscriptionModal = true"
              >
                Cancel Subscription
              </span>
            </div>
            <div
              v-if="isSubscriptionActive && isSubscriptionEnding"
              class="section-subtitle"
            >
              Renew Your Subscription
            </div>
            <div
              v-if="isSubscriptionActive && isSubscriptionEnding"
              class="section-row"
            >
              <span class="renew-subscription-message">
                Your subscription will end on {{ renewSubscriptionMessage }}. You'll lose all team data.
              </span>
              <span
                class="button"
                @click="showRenewSubscriptionModal = true"
              >
                Renew Subscription
              </span>
            </div>
          </div>
        </div>
        <div
          v-if="!isTeamLeader"
          class="section"
        >
          <div class="section-body">
            <div class="section-subtitle">
              Leave This Team
            </div>
            <div class="section-row">
              <span>
                You'll lose your points with this team and fall to the basic plan.
              </span>
              <!-- <span
                class="button danger"
                @click="leaveTeam"
              >
                Leave Team
              </span> -->
              <SpinnerButton
                class="button danger"
                :isProcessing="isRedirectingToPlans"
                @click="leaveTeam"
              >
                Leave Team
              </SpinnerButton>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <InviteLinkModal
      v-if="showInviteLinkModal"
      @close="showInviteLinkModal = false"
    />
    <CancelSubscriptionModal
      v-if="showCancelSubscriptionModal"
      @close="showCancelSubscriptionModal = false"
    />
    <RenewSubscriptionModal
      v-if="showRenewSubscriptionModal"
      @close="showRenewSubscriptionModal = false"
    />
    <ChangeNumSeatsModal
      v-if="showChangeNumSeatsModal"
      @close="showChangeNumSeatsModal = false"
    />
    <div class="spinner-top-padded" v-if="!show">
      <Spinner color="teal" />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import InviteLinkModal from '@/components/pricingAndTeamComponents/InviteLinkModal.vue';
import CancelSubscriptionModal from '@/components/pricingAndTeamComponents/CancelSubscriptionModal.vue';
import RenewSubscriptionModal from '@/components/pricingAndTeamComponents/RenewSubscriptionModal.vue';
import ChangeNumSeatsModal from '@/components/pricingAndTeamComponents/ChangeNumSeatsModal.vue';
import Spinner from '@/components/utilComponents/Spinner.vue';
import SpinnerButton from '@/components/utilComponents/SpinnerButton';

const defaultProfilePicture = require('@/assets/default-profile-picture.jpg');

export default Vue.extend({
  name: 'TeamAccountView',
  components: {
    InviteLinkModal,
    CancelSubscriptionModal,
    RenewSubscriptionModal,
    ChangeNumSeatsModal,
    SpinnerButton,
    Spinner
  },
  data: () => ({
    show: false,
    inviteLink: '',
    isUserOnTeam: false,
    isUserLeader: false,
    teamInfo: {
      members: []
    },
    isTeamInfoAvailable: false,
    newTeamName: '',
    isEditingTeamName: false,
    teamNameCharLimit: 24,
    subscriptionInfo: {},
    isRedirectingToPortal: false,
    isRedirectingToPlans: false,
    isSubscriptionInfoAvailable: false,
    showInviteLinkModal: false,
    showCancelSubscriptionModal: false,
    showRenewSubscriptionModal: false,
    showChangeNumSeatsModal: false,
    hasUnpaidMessageBeenDismissed: false,
    defaultProfilePicture
  }),
  computed: {
    auth0UserId: function () {
      return this.$auth.user.sub.replace(/\|/g, '_');
    },
    idToken: function () {
      return this.$auth.idToken;
    },
    isTeamLeader: function() {
      return 'leader' in this.teamInfo && this.teamInfo.leader.id === this.auth0UserId;
    },
    subscriptionStatus: function() {
      if (this.isSubscriptionInfoAvailable) {
        return this.subscriptionInfo.status || 'incomplete';
      }

      return 'incomplete';
    },
    isSubscriptionActive: function() {
      return ['trialing', 'active', 'past_due', 'unpaid'].includes(this.subscriptionStatus);
    },
    isSubscriptionUnpaid: function() {
      return ['past_due', 'unpaid'].includes(this.subscriptionStatus);
    },
    isSubscriptionEnding: function() {
      if (this.isSubscriptionInfoAvailable) {
        return this.subscriptionInfo.cancelAtPeriodEnd || false;
      }

      return false;
    },
    isEnterpriseSubscription: function() {
      if (this.isSubscriptionInfoAvailable) {
        return this.subscriptionInfo.hasUnlimitedSeats || false;
      }

      return false;
    },
    renewSubscriptionMessage: function() {
      if (this.isSubscriptionEnding && this.isSubscriptionInfoAvailable && 'currentPeriodEnd' in this.subscriptionInfo) {
        return new Date(this.subscriptionInfo.currentPeriodEnd * 1000).toLocaleString('default', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      }

      return '';
    },
    showUnpaidMessage: function() {
      return this.isTeamLeader && this.isSubscriptionUnpaid && !this.hasUnpaidMessageBeenDismissed;
      // return this.isTeamLeader && !this.hasUnpaidMessageBeenDismissed;
    },
    plan: function() {
      if (this.subscriptionInfo.hasUnlimitedSeats) {
        return 'Enterprise'
      } else if(this.subscriptionInfo.numStarterSeats || this.subscriptionInfo.numAdditionalSeats) {
        return 'Pro'
      }
      return 'Basic';
    },
    totalNumberOfSeats: function() {
      if (this.subscriptionInfo.hasUnlimitedSeats) {
        return 'unlimited'
      } else if(this.subscriptionInfo.numStarterSeats || this.subscriptionInfo.numAdditionalSeats) {
        return this.subscriptionInfo.numStarterSeats + this.subscriptionInfo.numAdditionalSeats
      }
      return 0;
    }
  },
  methods: {
    clipInputString() {
      if (this.maxLength && this.value.length > this.maxLength) {
        this.value = this.value.slice(0, this.maxLength);
      }
    },
    async saveTeamName() {
      if (!this.newTeamName) {
        return;
      }
      this.isEditingTeamName = false;
      this.teamInfo.teamName = this.newTeamName;
      await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/updateteamname`, {
        auth0UserId: this.auth0UserId,
        teamName: this.newTeamName
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });
    },
    async cancelTeamName() {
      this.isEditingTeamName = false;
      this.newTeamName = this.teamInfo.teamName;
    },
    async redirectToPortal() {
      try {
        this.isRedirectingToPortal = true;
        const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/createportalsession`, {
          auth0UserId: this.auth0UserId
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
        window.location.href = response.data.portalSessionUrl;
      } catch (error) {
        this.isRedirectingToPortal = false;
        console.log('Error:', error);
      }
    },
    async checkUserIsOnTeam() {
      const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/checkuserisonteam`, {
        auth0UserId: this.auth0UserId
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });
      this.isUserOnTeam = response.data.isUserOnTeam;
      this.isUserLeader = response.data.isUserLeader;
    },
    async getTeamInfo() {
      const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getteaminfo`, {
        auth0UserId: this.auth0UserId
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });
      this.teamInfo = response.data.teamInfo;
      this.isTeamInfoAvailable = true;
      this.$emit('check-user-is-on-team');
    },
    async getSubscriptionInfo(auth0UserId = this.auth0UserId) {
      const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getsubscriptioninfo`, {
        auth0UserId: auth0UserId
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });
      this.subscriptionInfo = response.data.subscriptionInfo;
      if (Object.keys(this.subscriptionInfo).length > 0) {
        this.isSubscriptionInfoAvailable = true;
      }
    },
    async verifyJWT() {
      try {
        await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/verifyjwt`, {
          token: this.teamInfo.token
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
      } catch (error) {
        console.log('Error:', error);
      }
    },
    async generateInviteLink() {
      try {
        const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/generateinvitelink`, {
          auth0UserId: this.auth0UserId
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
        this.inviteLink = response.data.inviteLink;
      } catch (error) {
        console.log('Error:', error);
      }
    },
    async updateTeamName() {
      try {
        await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/updateteamname`, {
          auth0UserId: this.auth0UserId,
          newTeamName: this.teamInfo.teamName
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
      } catch (error) {
        console.log('Error:', error);
      }
    },
    async leaveTeam() {
      try {
        // Check that the team hasn't been disbanded while the user has been viewing this page
        await this.checkUserIsOnTeam();

        if (!this.isUserOnTeam) {
          this.$router.push({
            name: 'pricing'
          });
        }
        await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/removeuserfromteam`, {
          auth0UserId: this.auth0UserId
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
        this.$emit('check-user-is-on-team');
        this.$router.push({
          name: 'home'
        });
      } catch (error) {
        console.log('Error:', error);
      }
    },
    async removeMemberFromTeam(memberIndex) {
      if ('members' in this.teamInfo) {
        const memberIdToRemove = this.teamInfo.members[memberIndex].id;
        if (memberIdToRemove && memberIdToRemove.length > 0) {
          try {
            await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/removeuserfromteam`, {
              auth0UserId: memberIdToRemove
            }, {
              headers: {
                'Content-Type': 'application/json', 
                'Authorization': `Bearer ${this.idToken}`
              }
            });
            await this.getTeamInfo();
          } catch (error) {
            console.log('Error:', error);
          }
        }
      }
    },
    async transferLeadershipToMember(memberIndex) {
      if ('members' in this.teamInfo) {
        const memberIdToMakeLeader = this.teamInfo.members[memberIndex].id;
        if (memberIdToMakeLeader && memberIdToMakeLeader.length > 0) {
          try {
            await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/transferleadershiptomember`, {
              auth0UserId: memberIdToMakeLeader
            }, {
              headers: {
                'Content-Type': 'application/json', 
                'Authorization': `Bearer ${this.idToken}`
              }
            });
            await this.getTeamInfo();
            await this.checkUserIsOnTeam();
          } catch (error) {
            console.log('Error:', error);
          }
        }
      }
    },
    editTeamName(){
      this.isEditingTeamName = true; 
      this.$nextTick(() => {
        this.$refs.teamNameInput.focus();
      });
    }
  },
  created: async function() {
    try {
      await this.checkUserIsOnTeam();
      if (!this.isUserOnTeam) {
        this.$router.push({
          name: 'pricing'
        });
      } else {
        // NOTE: else block is required here because the created function will run to completion before the router pushes
        await this.getTeamInfo();
        this.newTeamName = this.teamInfo.teamName;
        if (this.isUserLeader) {
          await this.getSubscriptionInfo();
        } else {
          await this.getSubscriptionInfo(this.teamInfo.leader.id);
        }
      }
      this.show = true;
    } catch (error) {
      console.log('Error:', error);
    }
  }
})
</script>

<style scoped lang="scss">
.container {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  color: $dark-brown;
  min-height: min-content;
  padding-top: 6em;
  padding-bottom: 6em;
  margin: 0 10em;
}

.section-body + .section-body {
  margin-top: 1.4em;
}

.container-content {
  height: max-content;
  width: 60%;
}

.member-entry {
  margin: 1em 0;
}

.member-item {
  display: flex;
  align-items: center;
}

.member-name {
  width: max-content;
  margin: 0 1em;
  font-size: 1.2em;
}

.button {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 2.2em;
  min-width: max-content;
  width: 11.2em;
  margin: 0 0.2em;
  color: $natural-white;
  background-color: $brand-teal;
  text-align: center;
  border-radius: 0.2em;
  box-shadow: 0 1px 1px 1px $light-gray;
  user-select: none;
  cursor: pointer;
  &:hover {
    background-color: $light-teal;
  }
  &.right {
    margin-left: auto;
  }
  &.narrow {
    width: 6em;
  }
  &.leader {
    width: 8em;
    background-color: $sapphire-blue;
  }
  &.leader:hover {
    background-color: $light-sapphire-blue;
  }
  &.danger {
    background-color: $crimson;
  }
  &.danger:hover {
    background-color: $light-crimson;
  }
  &.full-width {
    width: 100%;
  }
  &.dismiss {
    color: $natural-white;
    background-color: $crimson;
    width: max-content;
    padding: 0 0.8em;
    margin-left: 1em;
  }
}
.name-item {
  font-size: 1.1em;
  width: max-content;
  padding: 0.4em 0.8em;
  margin: 0.6em 0.6em 0.6em 0.1em;
}

.edit-icon-container {
  display: flex;
  flex: 1;
  font-size: 1.4em;
  margin: auto 0.6em;
}

.edit-icon {
  color: $brand-teal;
  cursor: pointer;
  &:hover {
    color: $light-teal;
  }
}

.team-name-input {
  flex: 1;
  min-width: 16em;
  padding: 0.4em 0.8em;
  margin: 0.6em 0.6em 0.6em 0;
  color: $dark-brown;
  font-family: inherit;
  font-size: 1.1em;
}

.char-limit-row {
  font-size: 0.8em;
  margin-left: 0.8em;
  color: $light-smoke-gray;
}

.char-limit-row.hidden {
  opacity: 0;
}

.pill {
  padding: 0.2em 0.8em;
  margin: 0 0.4em;
  height: max-content;
  width: max-content;
  font-size: 0.8em;
  color: $sapphire-blue;
  border: 0.2em solid $sapphire-blue;
  border-radius: 0.4em;
  user-select: none;
  &.danger {
    color: $crimson;
    border: 0.2em solid $crimson;
  }
}

.disabled {
  background-color: $light-gray!important;
  pointer-events: none;
}

.unpaid-message {
  display: flex;
  align-items: center;
  width: max-content;
  color: $crimson;
  border: 0.2em solid $crimson;
  border-radius: 0.4em;
  padding: 0.5em 1em;
  margin: 0.5em auto;
}

.renew-subscription-message {
  color: $crimson;
}
</style>
